import { forwardRef, memo, useCallback, useEffect, useRef, type RefObject } from 'react';
import useMeasure from 'react-use-measure';
import { ClientOnly } from 'remix-utils/client-only';
import { FileAttachments } from './FileAttachments.client';
import { FileSelections } from './FileSelections.client';
import { InspectorToggle } from './InspectorToggle.client';
import { PromptFocusedFiles } from './PromptFocusedFiles.client';
import { PromptInspectedElement } from './PromptInspectedElement.client';
import { PromptSubscribeBanner } from './PromptSubscribeBanner.client';
import { SendButton } from './SendButton.client';
import { FeaturePreviews } from '~/components/chat/FeaturePreviews.client';
import { PromptActionsButton } from '~/components/chat/PromptActionsButton';
import { PromptAgentSelectContainer } from '~/components/chat/PromptAgentSelectContainer';
import { PromptOverlay } from '~/components/chat/PromptOverlay.client';
import { PromptLibrary } from '~/components/chat/commands/PromptLibrary';
import { PromptDropZone } from '~/components/ui/PromptDropZone.client';
import { isBoltAgent } from '~/lib/agents';
import { chatHooks } from '~/lib/chat-hooks';
import { useChatAgent } from '~/lib/hooks/useChatAgent';
import { useDebounce } from '~/lib/hooks/useThrottle';
import type { PromptMode } from '~/lib/prompts/types';
import type { FileAttachment, FileSelection } from '~/types/chat';
import { isValidAttachment } from '~/utils/attachments.client';
import { waitForChameleon, showChameleonHelpbar } from '~/utils/chameleon';
import { classNames } from '~/utils/classNames';
import { SUPPORTED_PROMPT_FILE_TYPES_LIST, SUPPORTED_PROMPT_FILE_TYPES_SET } from '~/utils/constants';
import { isInspectedImage } from '~/utils/inspect-element';
import { wait } from '~/utils/promises';
import styles from './Prompt.module.scss';

// lines * line height + padding-top
const TEXTAREA_MIN_HEIGHT = 3 * 20 + 20;

export interface PromptProps {
  isReadOnly?: boolean;
  textareaRef?: RefObject<HTMLTextAreaElement>;
  chatStarted?: boolean;
  isDragging?: boolean;
  isStreaming?: boolean;
  disableInput?: boolean;
  enhancingPrompt?: boolean;
  promptEnhanced?: boolean;

  input?: string;
  fileSelections?: FileSelection[];
  attachments?: FileAttachment[];
  mode?: PromptMode;
  onModeChange?: (mode: PromptMode) => void;
  handleDrop?: (event: React.DragEvent<HTMLDivElement>) => Promise<void>;
  onUploadFile?: (file: File) => Promise<void> | void;
  onRemoveFileSelection?: (index: number) => void;
  onRemoveAttachment?: (attachment: FileAttachment) => void;
  handleStop?: () => void;
  handleInputChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  enhancePrompt?: () => void;
}

export const Prompt = memo(
  forwardRef<HTMLDivElement, PromptProps>((props, forwardedRef) => {
    const {
      textareaRef,
      isReadOnly,
      chatStarted = false,
      isDragging = false,
      isStreaming = false,
      enhancingPrompt = false,
      disableInput = false,
      fileSelections = [],
      input = '',
      mode = 'build',
      onModeChange,
      handleDrop,
      onUploadFile,
      onRemoveFileSelection,
      onRemoveAttachment,
      enhancePrompt,
      handleStop,
      handleInputChange,
    } = props;

    const [measureRef, bounds] = useMeasure();
    const { agent } = useChatAgent();

    useEffect(() => {
      document.documentElement.style.setProperty('--computed-prompt-box-height', `${bounds.height}px`);
    }, [bounds]);

    let placeholder = mode === 'build' ? 'How can Bolt help you today? (or /command)' : 'What do you want to discuss?';

    if (!chatStarted && mode === 'build') {
      placeholder = "Type your idea and we'll build it together.";
    }

    const attachments = props.attachments?.filter((attachment) => !isInspectedImage(attachment.file.name)) ?? [];

    const TEXTAREA_MAX_HEIGHT = chatStarted ? 400 : 200;

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handlePaste = useCallback(
      async (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const { items } = event.clipboardData;

        for (let i = 0; i < items.length; i++) {
          if (items[i].type.includes('image')) {
            const file = items[i].getAsFile();

            if (!isValidAttachment(file, { allowedExtensions: SUPPORTED_PROMPT_FILE_TYPES_SET })) {
              break;
            }

            if (file) {
              event.preventDefault();

              onUploadFile?.(file);

              break;
            }
          }
        }
      },
      [onUploadFile],
    );

    const handleSelectFile = useCallback(() => {
      fileInputRef.current?.click();
    }, [fileInputRef]);

    const handleFilesSelected = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (files) {
          for (const file of Array.from(files)) {
            onUploadFile?.(file);
          }
        }

        event.target.value = '';
      },
      [onUploadFile],
    );

    const hasAttachments = fileSelections.length > 0 || attachments.length > 0;

    const setInput = useCallback(
      (input: string) => {
        const textarea = textareaRef?.current;

        if (!textarea) {
          return;
        }

        if (input === '') {
          textarea.value = input;

          handleInputChange?.({ target: textarea } as React.ChangeEvent<HTMLTextAreaElement>);

          return;
        }

        textarea.focus();
        textarea.select();

        document.execCommand('insertText', false, input);

        handleInputChange?.({ target: textarea } as React.ChangeEvent<HTMLTextAreaElement>);

        textarea.selectionStart = input.length;
        textarea.selectionEnd = input.length;
      },
      [handleInputChange],
    );

    const focusPromptInput = useCallback(() => {
      requestAnimationFrame(() => {
        textareaRef?.current?.focus();
      });
    }, [textareaRef]);

    const handleSendMessage = () => {
      chatHooks.sendMessage?.();
      focusPromptInput(); // keep focus on input after sending message
    };

    const handleShowHelpbar = useCallback(() => {
      waitForChameleon().then(() => {
        showChameleonHelpbar();
      });
    }, []);

    const debouncedShowHelpbar = useDebounce(handleShowHelpbar, 300);

    return (
      <div
        ref={forwardedRef}
        className={classNames('relative px-[var(--chat-padding)] w-full max-w-chat mx-auto z-prompt', {
          'sticky bottom-0 bg-bolt-elements-background-depth-1': chatStarted,
        })}
      >
        {chatStarted && <PromptLibrary input={input} setInput={setInput} focusPromptInput={focusPromptInput} />}

        <ClientOnly>{() => <PromptSubscribeBanner />}</ClientOnly>

        <div className="relative shadow-xs p-[1px] overflow-clip rounded-lg">
          {chatStarted && (
            <>
              <div className="absolute inset-0 bg-bolt-elements-prompt-border -z-1" />
            </>
          )}
          <div
            className={classNames({
              'bg-bolt-elements-prompt-background rounded-lg dark:shadow-lg': chatStarted,
              'relative rounded-lg dark:bg-[rgba(31,31,31,0.95)] bg-white backdrop-blur-[7px]': !chatStarted,
              [styles.GradientBorder]: !chatStarted,
            })}
          >
            <ClientOnly>
              {() => {
                return (
                  <>
                    <PromptDropZone show={isDragging} onDrop={handleDrop} disabled={isReadOnly} />

                    <PromptOverlay />

                    {(fileSelections.length > 0 || attachments.length > 0) && (
                      <div className="flex flex-col gap-5 bg-bolt-elements-background-depth-1 py-5 rounded-t-lg">
                        {fileSelections.length > 0 && (
                          <div className="max-h-[50vh] overflow-y-auto">
                            <FileSelections
                              className="px-5"
                              itemClassName="bg-bolt-elements-prompt-selections-background"
                              buttonClassName="text-bolt-elements-prompt-selections-text hover:text-bolt-elements-prompt-selections-textHover"
                              fileSelections={fileSelections}
                              allowDelete
                              onFileSelectionDelete={onRemoveFileSelection}
                            />
                          </div>
                        )}
                        {attachments.length > 0 && (
                          <FileAttachments
                            className="flex pt-[9px] gap-5 px-5 overflow-x-auto" // the padding top is needed because of the overflow, it's half the size of the close button
                            attachments={attachments}
                            onRemoveAttachment={onRemoveAttachment}
                          />
                        )}
                      </div>
                    )}
                  </>
                );
              }}
            </ClientOnly>

            <ClientOnly fallback={<div className="border-b-px border-transparent" />}>
              {() => (
                <>
                  <PromptInspectedElement hasAttachments={hasAttachments} />
                  <PromptFocusedFiles hasAttachments={hasAttachments} />
                </>
              )}
            </ClientOnly>

            <div className="relative select-none">
              <textarea
                ref={textareaRef}
                disabled={disableInput || isReadOnly}
                className={classNames(
                  'w-full pl-5 pt-5 pr-16 focus:outline-none resize-none text-md text-bolt-elements-textPrimary placeholder-bolt-elements-textTertiary bg-transparent text-sm',
                  {
                    'hover:cursor-not-allowed': disableInput || isReadOnly,
                  },
                )}
                onMouseDown={(event) => event.stopPropagation()}
                onPaste={handlePaste}
                onKeyDown={(event) => {
                  if (
                    event.key !== 'Enter' ||
                    event.shiftKey ||
                    event.nativeEvent.isComposing ||
                    event.keyCode === 229
                  ) {
                    return;
                  }

                  if (event.currentTarget.value.startsWith('/')) {
                    return;
                  }

                  event.preventDefault();

                  handleSendMessage();
                }}
                value={input}
                onChange={(event) => {
                  handleInputChange?.(event);
                }}
                style={{
                  minHeight: TEXTAREA_MIN_HEIGHT,
                  maxHeight: TEXTAREA_MAX_HEIGHT,
                }}
                placeholder={placeholder}
                translate="no"
              />
            </div>

            <div className="flex justify-between text-sm px-3 pb-3 pt-2">
              <div className="flex gap-1 items-center w-full">
                <PromptActionsButton
                  chatStarted={chatStarted}
                  actions={[
                    {
                      icon: 'i-heroicons-outline:document-plus',
                      label: 'Attach file',
                      onClick: handleSelectFile,
                    },
                    {
                      icon: 'i-bolt:slash-command',
                      label: 'Open Prompt Library',
                      onClick: async () => {
                        await wait(1);
                        setInput('/');
                        await wait(1);
                        focusPromptInput();
                      },
                      disabled: !!input.length || isReadOnly,
                      hidden: !chatStarted,
                    },
                    {
                      icon: 'i-ph:chats',
                      label: 'Discussion Mode',
                      active: mode === 'discussion',
                      disabled: isStreaming || !onModeChange || isReadOnly,
                      hidden: !isBoltAgent(agent),
                      onClick: () => onModeChange?.(mode === 'discussion' ? 'build' : 'discussion'),
                    },
                    {
                      icon: enhancingPrompt ? 'i-svg-spinners:90-ring-with-bg' : 'i-bolt:stars',
                      label: enhancingPrompt ? 'Enhancing prompt...' : 'Enhance prompt',
                      onClick: () => enhancePrompt?.(),
                      disabled: input.length === 0 || enhancingPrompt || input.startsWith('/') || isReadOnly,
                    },
                    {
                      icon: 'i-ph:question',
                      label: 'Search Help Center',
                      onClick: debouncedShowHelpbar,
                      disabled: !!input.length,
                    },
                  ]}
                />

                {!chatStarted && (
                  <div className="ml-1">
                    <PromptAgentSelectContainer />
                  </div>
                )}

                {chatStarted && <ClientOnly>{() => <InspectorToggle />}</ClientOnly>}

                <div className="flex-1" />

                <ClientOnly>
                  {() => (
                    <SendButton
                      disabled={disableInput || (!input.length && !isStreaming)}
                      isStreaming={isStreaming}
                      onClick={() => {
                        if (isStreaming) {
                          handleStop?.();
                          return;
                        }

                        handleSendMessage();
                      }}
                    />
                  )}
                </ClientOnly>

                <input
                  type="file"
                  aria-hidden="true"
                  tabIndex={-1}
                  ref={fileInputRef}
                  style={{ display: 'none', visibility: 'hidden' }}
                  accept={SUPPORTED_PROMPT_FILE_TYPES_LIST}
                  multiple
                  onChange={handleFilesSelected}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                />
              </div>

              <div className="flex items-center gap-3">
                <ClientOnly>{() => <FeaturePreviews />}</ClientOnly>
              </div>
            </div>
          </div>
        </div>
        <div className="pb-[var(--chat-padding)]" />
        <div ref={measureRef} className="absolute inset-0 pointer-events-none"></div>
      </div>
    );
  }),
);
